import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "../../utils/appToast";
import { useNavigate } from "react-router-dom";
import ErrorPopup from "../chat/ErrorPopup";
import { getFreshAccessToken } from "../chat/api";
import api from "../chat/api";

const UploadModal = ({
  isOpen,
  onClose,
  onUploadSuccess,
  onError,
  type,
  folderId,
  file,
}) => {
  const [files, setFiles] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [searchTag, setSearchTag] = useState("");
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("");
  const [newTagDescription, setNewTagDescription] = useState("");
  const [showTagSection, setShowTagSection] = useState(false);
  const [showCreateTagForm, setShowCreateTagForm] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isFatalError, setIsFatalError] = useState(false);
  
  // We use a ref to track files for cancellation because state updates 
  // might not be fast enough during the close event
  const filesRef = useRef([]); 
  const selectedTagsRef = useRef([]);
  const fileInputRef = useRef(null);
  const isMountedRef = useRef(true);
  
  const STATUS = { 
    PENDING: 'pending', 
    UPLOADING: 'uploading', 
    SUCCESS: 'success', 
    ERROR: 'error' 
  };

  const navigate = useNavigate();
  const MAX_FILES = 5;
  const MAX_TAGS = 3;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Keep filesRef in sync with state for the close handler
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    selectedTagsRef.current = selectedTags;
  }, [selectedTags]);

  useEffect(() => {
    if (!isOpen) return;

    loadTags();
    setFiles([]);
    setShowErrorPopup(false);
    setErrorMessage("");

    if (file && Array.isArray(file) && file.length > 0) {
      const meta = file.map((f) => ({
        file: f,
        progress: 0,
        status: STATUS.PENDING,
        key: null,
        ws: null,
        file_id: null,
        errorReason: null,
        controller: new AbortController(), // Add Controller for cancellation
      }));

      setFiles(meta);
      startBatchUpload(meta);
    }
  }, [isOpen, file]);

  const loadTags = async () => {
    try {
      const res = await api.get("/api/tagging/tags/");
      if (isMountedRef.current) {
        setAllTags(res.data);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to load tags.";
      toast.error(msg, { position: "top-right", autoClose: 4000 });
    }
  };

  const handleCreateNewTag = async () => {
    if (!newTagName.trim() || !newTagColor) {
      toast.error("Please fill in all required fields (Name and Color).", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      const res = await api.post("/api/tagging/tags/", {
        name: newTagName,
        color: newTagColor,
        description: newTagDescription,
      });

      const newTag = res.data;
      if (isMountedRef.current) {
        setSelectedTags((prev) => [...prev, newTag]);
        setNewTagName("");
        setNewTagColor("");
        setNewTagDescription("");
        setTimeout(() => setShowCreateTagForm(false), 100);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to create tag.";
      toast.error(msg, { position: "top-right", autoClose: 4000 });
    }
  };

  const handleTagSelection = (tag) => {
    if (selectedTags.length >= MAX_TAGS) {
      toast.warning(`You can only select up to ${MAX_TAGS} tags.`, {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    setSelectedTags((prev) => [...prev, tag]);
  };

  const buildUploadURL = (t, folderId) => {
    const base = process.env.REACT_APP_API_URL;
    switch (t) {
      case "contract": return `${base}api/v1/readpdf/qdrant-upload-async/`;
      case "ai-copilot": return `${base}api/v1/readpdf/qdrant-upload-async/`;
      case "Invoices": return `${base}api/v1/readpdf/qdrant-upload-async/`;
      case "Spreadsheets": return `${base}api/v1/readpdf/qdrant-upload-async/`;
      case "ocr": return `${base}api/v1/idsocr/qdrant-upload-id/`;
      case "underFolder": return `${base}api/v1/readpdf/upload-in-folder/${folderId}/`;
      case "rfp_knowledge": return `${base}api/v1/rfp/knowledge-base/upload/`;
      case "rfp_analyze": return `${base}api/v1/rfp/upload/`;
      default: return `${base}api/v1/readpdf/qdrant-upload-async/`;
    }
  };

  async function startBatchUpload(fileList) {
    const formData = new FormData();
    fileList.forEach(f => formData.append("files", f.file));

    const token = await getFreshAccessToken();
    const url = buildUploadURL(type, folderId);
    
    // We use the controller from the first file for the batch request
    const mainController = fileList[0].controller || new AbortController();

    const ws = new WebSocket(
      `${process.env.REACT_APP_WS_URL}/ws/upload-status/?token=${token}`
    );

    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (!isMountedRef.current) return;

      setFiles(prevFiles => {
        let alreadyClaimed = false;
        const updatedFiles = prevFiles.map((f, i) => {
          const isKeyMatch = f.key === data.pdf_key;
          const canClaim =
            !alreadyClaimed &&
            !f.key &&
            f.status === STATUS.UPLOADING &&
            data.pdf_key &&
            !prevFiles.some((other, idx) => other.key === data.pdf_key && idx !== i);

          if (!isKeyMatch && !canClaim) return f;

          const patch = {};
          if (canClaim) {
            patch.key = data.pdf_key;
            alreadyClaimed = true;
          }

          if (data.type === "progress") {
            patch.progress = data.progress;
          } else if (data.type === "completed") {
            if (!data.metadata?.file_id) return f;
            patch.status = STATUS.SUCCESS;
            patch.progress = 100;
            patch.file_id = data.metadata.file_id;
          } else if (data.type === "error") {
            patch.status = STATUS.ERROR;
            patch.progress = 0;
            const msg = data.message?.toLowerCase() || "";
            if (msg.includes("unsupported")) patch.errorReason = "unsupported";
            else if (msg.includes("duplicate")) patch.errorReason = "duplicate";
            else if (msg.includes("size") || msg.includes("limit")) patch.errorReason = "size_limit";
            else patch.errorReason = "unknown";
          }
          return { ...f, ...patch };
        });

        const allDone = updatedFiles.every(
          f => f.status === STATUS.SUCCESS || f.status === STATUS.ERROR
        );
        
        if (allDone && ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
        return updatedFiles;
      });
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      if (isMountedRef.current) {
        setFiles(prev => prev.map(f => ({ 
          ...f, 
          status: STATUS.ERROR,
          errorReason: "websocket_error"
        })));
      }
    };

    try {
      if (isMountedRef.current) {
        setFiles(prev => prev.map(f => 
          fileList.some(newFile => newFile.file.name === f.file.name) 
            ? { ...f, status: STATUS.UPLOADING } 
            : f
        ));
      }

      const response = await api.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        signal: mainController.signal, // Attach AbortSignal here
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && isMountedRef.current) {
            const baseProgress = Math.min(30, Math.round((progressEvent.loaded * 100) / progressEvent.total));
            setFiles(prev => prev.map(f => {
              return { ...f, progress: Math.max(f.progress, baseProgress) };
            }));
          }
        },
      });

      const uploads = response.data?.uploads || [];
      if (isMountedRef.current) {
        setFiles(prevFiles =>
          prevFiles.map(f => {
            const upload = uploads.find(u => u.filename === f.file.name);
            return upload && upload.pdf_key
              ? { ...f, key: upload.pdf_key }
              : f;
          })
        );
      }

    } catch (error) {
      // If cancelled, do not treat as a standard error
      if (axios.isCancel(error) || error.name === "CanceledError") {
         console.log("Upload cancelled by user");
         return;
      }

      console.error("🚨 Batch upload failed:", error);
      const statusCode = error?.response?.status;
      const errorMessage = error?.response?.data?.error || "An unknown error occurred.";

      if (isMountedRef.current) {
        if ((statusCode === 400 || statusCode === 403) &&
            (errorMessage.includes("Trial users can upload") || errorMessage.includes("You have reached your upload limit"))) {
          setErrorMessage(errorMessage);
          setShowErrorPopup(true);
          setIsFatalError(true);
        } else {
          setFiles(prev => prev.map(f => ({ 
            ...f, 
            status: STATUS.ERROR,
            errorReason: "upload_failed"
          })));
        }
      }
      if (ws.readyState === WebSocket.OPEN) ws.close();
    }
  }

  const onFileSelect = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;

    if (picked.length + files.length > MAX_FILES) {
      setErrorMessage(`You can upload max ${MAX_FILES} files at once.`);
      setShowErrorPopup(true);
      return;
    }

    const meta = picked.map(f => ({
      file: f,
      progress: 0,
      status: STATUS.PENDING,
      key: null,
      ws: null,
      file_id: null,
      errorReason: null,
      controller: new AbortController(), // New controller for new files
    }));

    setFiles(prevFiles => [...prevFiles, ...meta]);
    setTimeout(() => {
      startBatchUpload(meta);
    }, 0);
  };

  const updateFile = (idx, patchOrFn) =>
    setFiles(prev => prev.map((f, i) =>
      i === idx
        ? typeof patchOrFn === "function" ? { ...f, ...patchOrFn(f) } : { ...f, ...patchOrFn }
        : f
    ));

  async function startUpload(fileObj, idx) {
    // Create a new controller for the retry
    const newController = new AbortController();
    updateFile(idx, { status: STATUS.UPLOADING, progress: 0, controller: newController });

    const formData = new FormData();
    formData.append("files", fileObj.file);

    const url = buildUploadURL(type, folderId);
    const token = await getFreshAccessToken();

    if (fileObj.ws && fileObj.ws.readyState === WebSocket.OPEN) {
      fileObj.ws.close();
    }

    const ws = new WebSocket(
      `${process.env.REACT_APP_WS_URL}/ws/upload-status/?token=${token}`
    );

    updateFile(idx, { ws });

    // WebSocket logic same as before...
    ws.onmessage = (e) => {
      const d = JSON.parse(e.data);
      if (!isMountedRef.current) return;

      setFiles((prevFiles) =>
        prevFiles.map((f, i) => {
          if (i !== idx) return f;
          // ... (same matching logic as before)
          const isSameFile = f.file.name === fileObj.file.name && (f.key === null || f.key === d.pdf_key);
          if (!isSameFile) return f;

          const patch = {};
          if (d.pdf_key && !f.key) patch.key = d.pdf_key;
          
          if (d.type === "progress") patch.progress = d.progress;
          else if (d.type === "completed") {
            if (!d.metadata?.file_id) return f;
            patch.status = STATUS.SUCCESS;
            patch.progress = 100;
            patch.file_id = d.metadata.file_id;
          } else if (d.type === "error") {
            patch.status = STATUS.ERROR;
            patch.progress = 0;
            // ... (error parsing logic same as before)
             const msg = d.message?.toLowerCase() || "";
            if (msg.includes("unsupported")) patch.errorReason = "unsupported";
            else if (msg.includes("duplicate")) patch.errorReason = "duplicate";
            else if (msg.includes("size") || msg.includes("limit")) patch.errorReason = "size_limit";
            else patch.errorReason = "unknown";
          }
          return { ...f, ...patch };
        })
      );
      
      if ((d.type === "completed" || d.type === "error") && d.pdf_key) {
        ws.close();
      }
    };

    try {
      const resp = await api.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        signal: newController.signal, // Attach Signal
        onUploadProgress: (ev) => {
          if (isMountedRef.current) {
            const pct = Math.min(30, Math.round((ev.loaded * 100) / ev.total));
            updateFile(idx, prev => ({ progress: Math.max(prev.progress, pct) }));
          }
        }
      });
      // ... (response handling same as before)
    } catch (err) {
       if (axios.isCancel(err) || err.name === "CanceledError") {
         console.log("Retry cancelled by user");
         return;
      }
      if (isMountedRef.current) {
        updateFile(idx, { status: STATUS.ERROR, errorReason: "upload_failed" });
      }
      ws.close();
    }
  }

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  const handleClose = async () => {
    let finalCancelledKeys = [];

    // Use current state via Ref to avoid closure staleness issues
    const currentFiles = filesRef.current; 
    const uploadingFiles = currentFiles.filter(f => f.status === STATUS.UPLOADING);
    
    // 1. Abort outgoing browser requests (for files that have no key yet)
    uploadingFiles.forEach(f => {
      // If we are still sending bytes to the server, abort the request
      if (f.controller) {
        f.controller.abort();
      }
      // Close any open sockets
      if (f.ws && f.ws.readyState === WebSocket.OPEN) {
        f.ws.close();
      }
    });

    // 2. Identify files that made it to the server (have a key) and need API cancellation
    const activeKeys = uploadingFiles
        .filter(f => f.key) // Only if they have a key
        .map(f => f.key);

    if (activeKeys.length > 0) {
      try {
        const res = await api.post(
          `/api/v1/rfp/knowledge-base/cancel-upload/`,
          { pdf_keys: activeKeys }
        );

        const cancelledResults = res.data.results || [];

        finalCancelledKeys = cancelledResults
          .filter(entry => entry.message !== "Upload not found or already completed.")
          .map(entry => entry.pdf_key);

      } catch (err) {
        console.error("🚨 Failed to cancel uploads:", err);
      }
    }

    // Update UI to show cancellation immediately for ALL uploading files
    if (isMountedRef.current) {
      setFiles(prev => prev.map(f => {
        if (f.status === STATUS.UPLOADING) {
          return { 
            ...f, 
            status: STATUS.ERROR, 
            errorReason: "cancelled",
          };
        }
        return f;
      }));
    }

    // Wait 2 seconds to let user see the message
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Tagging logic for SUCCESSFUL files
    if (selectedTagsRef.current.length > 0) {
      try {
        const taggedDocs = filesRef.current.filter(f => f.status === STATUS.SUCCESS && f.file_id);
        for (const tag of selectedTagsRef.current) {
          for (const file of taggedDocs) {
            await api.post(
              `api/tagging/tagged-items/`,
              {
                tag_id: tag.id,
                pdf_document_id: file.file_id,
                folder_id: null,
              }
            );
          }
        }
      } catch (err) {
        console.error("❌ Tagging failed:", err);
      }
    }

    if (typeof onUploadSuccess === "function") {
      onUploadSuccess();
    }

    setFiles([]);
    setSelectedTags([]);
    onClose();

    if (type === "ocr") {
      navigate("/Ocr_data");
    } else if (type === "underFolder") {
      navigate(`/folder/?folderId=${folderId}`);
    } else if (type === "rfp_knowledge") {
      navigate("/rfp-knowledgeBase");
    } else if (type === "rfp_analyze") {
      navigate("/rfp-analyze");
    } else {
      navigate("/uploadFile", {
        state: {
          cancelledPdfKeys: finalCancelledKeys,
        },
      });
    }
  };

  const handleAddMore = () => {
    triggerFileInput();
  };

  const handleTagNameChange = (e) => {
    const value = e.target.value;
    const alphanumericOnly = value.replace(/[^a-zA-Z0-9\s]/g, '');
    setNewTagName(alphanumericOnly);
  };

  // Icons
  const CloseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const CheckIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="#22C55E"/>
    </svg>
  );

  const ErrorIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="#EF4444"/>
    </svg>
  );

  const PlusIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 4.16669V15.8334M4.16669 10H15.8334" stroke="#C95EBE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  if (!isOpen) return null;

  const allUploadsComplete = files.length > 0 && files.every(
    f => f.status === STATUS.SUCCESS || f.status === STATUS.ERROR
  );

  return (
    <div>
      <div className="fixed inset-0 z-50 flex font-outfit items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
        {!isFatalError && (
          <div className="relative w-full max-w-md p-6 bg-white rounded-lg shadow-xl mx-4">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
              aria-label="Close"
            >
              <CloseIcon />
            </button>

            <input
              id="fileInput"
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              accept=".pdf,.xls,.xlsx,.csv,.txt,.doc,.jpeg,.jpg,.png,.webp,.bmp"
              onChange={onFileSelect}
            />

            {/* Header */}
            <div className="pr-8">
              <h2 className="text-xl font-semibold text-gray-800">
                {allUploadsComplete
                  ? type === "contract" ? "Contracts Uploaded" : "Files Uploaded"
                  : type === "contract" ? "Uploading Your Contracts" : "Uploading your Documents"}
              </h2>
              {!allUploadsComplete && (
                <p className="text-sm text-gray-600 mt-1">
                  Please wait while we extract data from your {files.length > 1 ? "files" : "file"}…
                </p>
              )}
            </div>

            {/* Scrollable File List */}
            <div className="mt-6 max-h-[200px] overflow-y-auto pr-2 space-y-4">
              {files.map((file, index) => (
                <div key={index} className="flex-shrink-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="flex-shrink-0">
                         {/* File Icon SVG */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <mask id={`mask0_775_15693_${index}`} style={{maskType: 'alpha'}} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                            <rect width="20" height="20" fill="#D9D9D9"/>
                          </mask>
                          <g mask={`url(#mask0_775_15693_${index})`}>
                            <path d="M7.5026 10.8307H12.5026C12.7387 10.8307 12.9366 10.7509 13.0964 10.5911C13.2561 10.4314 13.3359 10.2335 13.3359 9.9974C13.3359 9.76128 13.2561 9.56337 13.0964 9.40365C12.9366 9.24392 12.7387 9.16406 12.5026 9.16406H7.5026C7.26649 9.16406 7.06858 9.24392 6.90885 9.40365C6.74913 9.56337 6.66927 9.76128 6.66927 9.9974C6.66927 10.2335 6.74913 10.4314 6.90885 10.5911C7.06858 10.7509 7.26649 10.8307 7.5026 10.8307ZM7.5026 13.3307H12.5026C12.7387 13.3307 12.9366 13.2509 13.0964 13.0911C13.2561 12.9314 13.3359 12.7335 13.3359 12.4974C13.3359 12.2613 13.2561 12.0634 13.0964 11.9036C12.9366 11.7439 12.7387 11.6641 12.5026 11.6641H7.5026C7.26649 11.6641 7.06858 11.7439 6.90885 11.9036C6.74913 12.0634 6.66927 12.2613 6.66927 12.4974C6.66927 12.7335 6.74913 12.9314 6.90885 13.0911C7.06858 13.2509 7.26649 13.3307 7.5026 13.3307ZM7.5026 15.8307H10.0026C10.2387 15.8307 10.4366 15.7509 10.5964 15.5911C10.7561 15.4314 10.8359 15.2335 10.8359 14.9974C10.8359 14.7613 10.7561 14.5634 10.5964 14.4036C10.4366 14.2439 10.2387 14.1641 10.0026 14.1641H7.5026C7.26649 14.1641 7.06858 14.2439 6.90885 14.4036C6.74913 14.5634 6.66927 14.7613 6.66927 14.9974C6.66927 15.2335 6.74913 15.4314 6.90885 15.5911C7.06858 15.7509 7.26649 15.8307 7.5026 15.8307ZM5.0026 18.3307C4.54427 18.3307 4.15191 18.1675 3.82552 17.8411C3.49913 17.5148 3.33594 17.1224 3.33594 16.6641V3.33073C3.33594 2.8724 3.49913 2.48003 3.82552 2.15365C4.15191 1.82726 4.54427 1.66406 5.0026 1.66406H10.9818C11.204 1.66406 11.4158 1.70573 11.6172 1.78906C11.8186 1.8724 11.9957 1.99045 12.1484 2.14323L16.1901 6.1849C16.3429 6.33767 16.4609 6.51476 16.5443 6.71615C16.6276 6.91753 16.6693 7.12934 16.6693 7.35156V16.6641C16.6693 17.1224 16.5061 17.5148 16.1797 17.8411C15.8533 18.1675 15.4609 18.3307 15.0026 18.3307H5.0026ZM15.0026 7.4974H12.0859C11.7387 7.4974 11.4436 7.37587 11.2005 7.13281C10.9575 6.88976 10.8359 6.59462 10.8359 6.2474V3.33073H5.0026V16.6641H15.0026V7.4974Z" fill="#73607B"/>
                          </g>
                        </svg>
                      </div>
                      <span className="truncate text-sm font-medium !text-[#171717]">
                        {file.file.name.length > 25
                          ? `${file.file.name.slice(0, 20)}...${file.file.name.slice(-10)}`
                          : file.file.name}
                      </span>
                    </div>
                    <div className="flex-shrink-0">
                      {file.status === STATUS.SUCCESS ? <CheckIcon /> : file.status === STATUS.ERROR ? <ErrorIcon /> : (
                        <span className="text-sm font-semibold text-gray-800">{Math.round(file.progress)}%</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ease-linear ${
                        file.status === STATUS.ERROR 
                          ? "bg-red-500" 
                          : file.status === STATUS.SUCCESS 
                          ? "bg-green-500"
                          : "bg-purple-600"
                      }`}
                      style={{
                        width: `${file.progress}%`,
                        background: file.status === STATUS.UPLOADING 
                          ? "linear-gradient(180deg, #C95EBE 0%, #8B55DD 100%)"
                          : undefined,
                      }}
                    ></div>
                  </div>
                  
                  {file.status === STATUS.ERROR && (
                    <div className="mt-1 flex items-center gap-2">
                      <p className="text-xs text-red-600">
                        {file.errorReason === "unsupported" ? "Unsupported file type"
                          : file.errorReason === "duplicate" ? "Duplicate file"
                          : file.errorReason === "size_limit" ? "File size exceeds limit"
                          : file.errorReason === "cancelled" ? "Your upload request was cancelled"
                          : "Upload failed"}
                      </p>
                      <button
                        className="text-xs text-red-500 underline font-semibold"
                        onClick={() => startUpload(file, index)}
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Tag Section (Standard Tagging Logic) */}
            {allUploadsComplete && (
              <div className="w-full mt-4">
                <div
                  className="text-[#73607B] text-sm font-semibold mb-1 cursor-pointer flex items-center gap-1"
                  onClick={() => setShowTagSection(!showTagSection)}
                >
                  <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '16px', fontWeight: 400, color: '#171717' }}>Add Tags (optional):</span>
                  <img src="/arrow-downs.svg" alt="Toggle" className={`w-6 h-6 transform transition-transform duration-200 ease-in-out ${showTagSection ? "rotate-180" : ""}`} />
                </div>

                {showTagSection && (
                  <div className="mt-3 overflow-y-auto max-h-[160px]" style={{ background: "#FFFFFF", border: "1px solid transparent" }}>
                    {selectedTags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {selectedTags.map((tag, index) => (
                          <span
                            key={index}
                            className="flex items-center text-sm px-3 py-1 rounded-full cursor-pointer"
                            style={{ backgroundColor: `${tag.color}1A`, border: `1.5px solid ${tag.color}`, color: tag.color, fontFamily: "Outfit" }}
                            onClick={() => setSelectedTags((prev) => prev.filter((t) => t.id !== tag.id))}
                          >
                            {tag.name} <span className="ml-1 text-xs">✕</span>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 px-4 py-[10px] border rounded-full w-full" style={{ border: "1px solid rgba(0, 0, 0, 0.10)", fontFamily: "Outfit" }}>
                      <img src="/search.svg" alt="Search" className="w-6 h-6" />
                      <input
                        type="text"
                        placeholder="Search or create tag…"
                        value={searchTag}
                        onChange={(e) => setSearchTag(e.target.value)}
                        className="flex-1 text-[14px] placeholder:text-[#9E9EA7] outline-none"
                        style={{ fontFamily: "Outfit", color: "rgba(82, 82, 91, 0.72)", fontWeight: 400, border: "none", background: "transparent" }}
                      />
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {allTags.filter((tag) => tag.name.toLowerCase().includes(searchTag.toLowerCase()) && !selectedTags.some((t) => t.id === tag.id)).map((tag) => (
                        <span
                          key={tag.id}
                          onClick={() => handleTagSelection(tag)}
                          className="flex items-center text-sm px-3 py-1 rounded-full cursor-pointer"
                          style={{ backgroundColor: `${tag.color}1A`, border: `1.5px solid ${tag.color}`, color: tag.color, fontFamily: "Outfit" }}
                        >
                          {tag.name} <span className="ml-1 text-xs">+</span>
                        </span>
                      ))}
                    </div>
                    <div className="mt-2">
                      <button
                        onClick={() => setShowCreateTagForm((prev) => !prev)}
                        className="flex items-center gap-2 px-3 py-1 rounded-full text-white text-sm"
                        style={{ backgroundColor: "#8B55DD", fontFamily: "Outfit", fontSize: "14px", fontWeight: 500 }}
                      >
                        <span className="text-white">Create New tag</span>
                        <img src="/arrow-downs-white.svg" alt="Toggle" className={`w-4 h-4 transition-transform duration-200 ease-in-out ${showCreateTagForm ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    {showCreateTagForm && (
                      <div className="flex flex-col gap-3 mt-3">
                        <div>
                          <label className="block mb-1" style={{ color: "#52525B", fontFamily: "Outfit", fontSize: "13px", fontWeight: 500 }}>Name <span className="text-red-500">*</span></label>
                          <input value={newTagName} onChange={handleTagNameChange} className="w-full border border-[rgba(0,0,0,0.10)] rounded-[6px] px-3 py-2 text-[13px] font-[Outfit] text-[rgba(82,82,91,0.64)]" placeholder="Eg: Contracts" />
                        </div>
                        <div>
                          <label className="block mb-1" style={{ color: "#52525B", fontFamily: "Outfit", fontSize: "13px", fontWeight: 500 }}>Color <span className="text-red-500">*</span></label>
                          <div className="flex flex-wrap gap-2">
                            {["#7FFF7A", "#7AFFCE", "#7AEDFF", "#7ABEFF", "#7A9FFF", "#B27AFF", "#E97AFF", "#FF7AD7", "#FF7AA5", "#FF7A7A", "#D8FF75", "#FFFFFF", "#F7F7F7"].map((color) => (
                              <div key={color} className="w-6 h-6 rounded cursor-pointer border" style={{ backgroundColor: color, outline: newTagColor === color ? "2px solid #333" : "none" }} onClick={() => setNewTagColor(color)} />
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block mb-1" style={{ color: "#52525B", fontFamily: "Outfit", fontSize: "13px", fontWeight: 500 }}>Description (Optional)</label>
                          <textarea value={newTagDescription} onChange={(e) => setNewTagDescription(e.target.value)} className="w-full h-[80px] border border-[rgba(0,0,0,0.10)] rounded-[6px] px-3 py-2 text-[13px] font-[Outfit] resize-none" placeholder="Eg: All the Contracts Signed by the company" />
                        </div>
                        <div className="mt-3 flex justify-end">
                          <button onClick={handleCreateNewTag} className="inline-flex px-6 py-2 justify-center items-center rounded-[9px] border border-[rgba(0,0,0,0.10)]" style={{ backgroundColor: "#FEF9F7", color: "#C95EBE", fontFamily: "Outfit", fontSize: "14px", fontWeight: 500 }}>Create tag</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            {allUploadsComplete && (
              <div className="mt-6 space-y-3">
                <button onClick={handleAddMore} className="flex items-center text-[#C95EBE] justify-center w-[30%] px-2 py-2 border !border-black/10 rounded-lg text-sm font-medium">
                  <PlusIcon />
                  <span className="ml-2 text-[#C95EBE]">Add More</span>
                </button>
              </div>
            )}

            <div className="mt-6">
              {allUploadsComplete ? (
                <div className="flex justify-end">
                  <button onClick={handleClose} className="px-6 py-2.5 bg-[#C95EBE] !text-white rounded-lg font-semibold text-sm">Done</button>
                </div>
              ) : (
                <div className="flex justify-between text-xs text-[#71717A]">
                  <span>Supported formats: PDF, TXT, CSV, XLS, DOCX, XLSX, JPG, JPEG, PNG</span>
                  <span>Maximum Size: 25MB</span>
                </div>
              )}
            </div>
          </div>
        )}
        <ErrorPopup
          show={showErrorPopup}
          message={errorMessage}
          onClose={() => {
            setShowErrorPopup(false);
            if (isFatalError) {
              onClose();
              setIsFatalError(false);
            } else {
              const allFailedOrNone = files.length === 0 || files.every(f => f.status === STATUS.ERROR);
              if (allFailedOrNone) {
                onClose();
              }
            }
          }}
        />
      </div>
    </div>
  );
};

export default UploadModal;
